/**
 * LeadFormModal — modal "Solicitar cita" en el perfil público.
 *
 * Captura nombre + tel/email + mensaje. Backend envía email al
 * notification_email del negocio + copia SuperAdmin + audit en `leads`.
 */
import { useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import TurnstileWidget from "./TurnstileWidget";
import { X, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function LeadFormModal({ slug, businessName, defaultService = "", onClose }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: defaultService, message: "",
  });
  const [tToken, setTToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.phone && !form.email) {
      setError("Indica al menos teléfono o email.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/centro/${slug}/lead`, { ...form, turnstile_token: tToken, honeypot: "" });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo enviar.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="lead-modal">
      <div className="relative ag-card max-w-md w-full p-6 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--ag-text-dim)] hover:text-white"
                data-testid="lead-close"><X size={18} /></button>
        {done ? (
          <div className="text-center py-6" data-testid="lead-success">
            <CheckCircle size={32} weight="fill" className="text-emerald-300 mx-auto" />
            <h3 className="font-display text-2xl font-bold mt-3 text-[var(--ag-text)]">¡Solicitud enviada!</h3>
            <p className="text-[var(--ag-text-dim)] text-sm mt-2">
              {businessName} recibirá tu mensaje y te contactará pronto.
            </p>
            <button onClick={onClose} className="ag-btn-gold mt-5 px-5 py-2 text-xs uppercase tracking-wider"
                    data-testid="lead-close-success">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
            <div>
              <EnvelopeSimple size={20} className="text-[var(--ag-gold)]" />
              <h3 className="font-display text-2xl font-bold tracking-tighter mt-2 text-[var(--ag-text)]">Solicitar cita</h3>
              <p className="text-[var(--ag-text-dim)] text-xs mt-1">Para {businessName}</p>
            </div>
            <Input placeholder="Tu nombre *" required minLength={2}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="lead-name" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Teléfono" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="lead-phone" />
              <Input type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="lead-email" />
            </div>
            <Input placeholder="Servicio que te interesa (opcional)"
              value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} />
            <textarea placeholder="Mensaje (opcional)" rows={3}
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-3 py-2.5 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none"
              data-testid="lead-message" />
            <TurnstileWidget onToken={setTToken} />
            {error && <div className="text-xs text-red-400" data-testid="lead-error">{error}</div>}
            <button type="submit" disabled={busy}
              className="ag-btn-gold w-full py-3 text-xs font-semibold tracking-wider uppercase"
              data-testid="lead-submit">
              {busy ? "Enviando…" : "Enviar solicitud"}
            </button>
            <p className="text-[10px] text-[var(--ag-text-dim)] text-center leading-relaxed" data-testid="lead-legal-notice">
              Tus datos se envían directamente al negocio. No los compartimos con terceros.
              <br />
              Al enviar aceptas la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Política de privacidad</a> y los <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Términos</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const Input = (props) => (
  <input {...props}
    className={`w-full h-11 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none ${props.className||""}`} />
);
